import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AdvancedFieldHistory,
  BandShootTime,
  ValueHistory,
  VersionShootTime,
} from "./versionShootTime";
import {
  BandBounds,
  formatFormulaFieldValue,
  getBandLabel,
  MULTIPLIER_FORMULA_FIELD_KEY,
} from "portal/utils/shootTime";
import { LocalizedList } from "../../LocalizedList";
import {
  ResolvedOriginCategory,
  translateCategoryName,
} from "portal/utils/categoryCollection/category";
import { StyleChange, ValueChange, ValueChangeRender } from "./ValueChange";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { FC, ReactNode } from "react";

interface ShootTimeHistorySectionProps {
  shootTime: VersionShootTime;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

export const ShootTimeHistorySection: FC<ShootTimeHistorySectionProps> = ({
  shootTime,
  expanded,
  onExpandedChange,
}) => {
  const { t } = useTranslation();
  const {
    palette: {
      chart: { bandStrokeColors },
    },
  } = useTheme();

  if (shootTime.bands.length === 0) {
    return;
  }

  const { bands, sharedAdvanced } = shootTime;

  return (
    <Paper elevation={0} className="rounded-sm">
      <Accordion
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={(_, open) => onExpandedChange(open)}
        className="before:hidden bg-transparent"
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className="min-h-0 px-3"
          classes={{ content: "my-2" }}
        >
          <Typography variant="body2" component="h2" className="uppercase">
            {t("views.categoryTemplates.tabs.shootTime")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className="flex flex-col gap-2 px-3 pb-3 pt-0">
          <MostSimilarToHistoryRow history={shootTime.mostSimilarTo} />
          {bands.map((band) => (
            <ShootTimeHistoryBandRow
              key={band.index}
              band={band}
              bandCount={bands.length}
              color={bandStrokeColors[band.index % bandStrokeColors.length]}
              advanced={sharedAdvanced ? undefined : band.advanced}
            />
          ))}
          {sharedAdvanced && <AdvancedLine fields={sharedAdvanced} />}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

/**
 * Unchanged history values render as-is. Diffs go through ValueChange, which
 * infers added / removed / replaced from before and after.
 */
const HistoryValue = <T,>({
  history,
  className,
  children,
}: {
  history: ValueHistory<T>;
  className?: string;
  children: ValueChangeRender<T>;
}): ReactNode => {
  if (history.change === "unchanged") {
    return history.after === undefined
      ? undefined
      : children(history.after, {});
  }
  return (
    <ValueChange
      before={history.before}
      after={history.after}
      className={className}
    >
      {children}
    </ValueChange>
  );
};

interface ShootTimeHistoryBandRowProps {
  band: BandShootTime;
  bandCount: number;
  color?: string;
  /** Omitted when every band shares the same fields and they are shown once. */
  advanced?: AdvancedFieldHistory[];
}

const ShootTimeHistoryBandRow: FC<ShootTimeHistoryBandRowProps> = ({
  band,
  bandCount,
  color,
  advanced,
}) => {
  const { t } = useTranslation();
  const label = getBandLabel(t, band.index, bandCount);

  return (
    <div className="flex gap-3 items-start">
      <div className="flex items-center gap-2 min-w-[6rem] shrink-0">
        <div
          className="w-3 h-3 rounded-sm shrink-0"
          style={{ backgroundColor: color }}
        />
        <Typography className="font-medium">{label}</Typography>
      </div>
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <HistoryValue history={band.bounds} className="flex-wrap">
            {(bounds, { styleChange }) => (
              <BoundsPill
                label={formatBounds(t, bounds)}
                color={color}
                styleChange={styleChange}
              />
            )}
          </HistoryValue>
          <MultiplierCallout history={band.multiplier} />
        </div>
        {advanced && <AdvancedLine fields={advanced} />}
      </div>
    </div>
  );
};

const useStyleChangeColor = (styleChange?: StyleChange): string | undefined => {
  const { palette } = useTheme();
  if (!styleChange) {
    return;
  }
  return styleChange === "added" ? palette.success.light : palette.error.light;
};

/**
 * Mirrors the chart's read-only threshold pills: outlined in the band color,
 * keeping that color when changed and ringing it in the style-change color
 * instead.
 */
const BoundsPill: FC<{
  label: string;
  color?: string;
  styleChange?: StyleChange;
}> = ({ label, color, styleChange }) => {
  const styleChangeColor = useStyleChangeColor(styleChange);

  return (
    <span
      className="shrink-0 border border-solid rounded-md px-1"
      style={{
        borderColor: color,
        color,
        outline: styleChangeColor ? `1px solid ${styleChangeColor}` : undefined,
        outlineOffset: 1,
      }}
    >
      <Typography
        component="span"
        variant="body2"
        className={styleChange === "removed" ? "line-through" : undefined}
      >
        {label}
      </Typography>
    </span>
  );
};

const MultiplierCallout: FC<{ history: ValueHistory<number> }> = ({
  history,
}) => {
  const { t } = useTranslation();

  if (history.change === "unchanged" && history.after === undefined) {
    return;
  }

  return (
    <span className="shrink-0 inline-flex items-center gap-1">
      <Typography component="span" variant="body2">
        {t(
          `views.categoryTemplates.shootTime.advancedFields.${MULTIPLIER_FORMULA_FIELD_KEY}.label`
        )}
      </Typography>
      <span className="inline-flex items-center border border-solid border-darken-200 dark:border-lighten-300 rounded-md px-1 py-0">
        <HistoryValue history={history}>
          {(value, { styleChange }) => (
            <MultiplierValue value={value} styleChange={styleChange} />
          )}
        </HistoryValue>
      </span>
    </span>
  );
};

const MultiplierValue: FC<{
  value: number;
  styleChange?: StyleChange;
}> = ({ value, styleChange }) => {
  const styleChangeColor = useStyleChangeColor(styleChange);

  return (
    <Typography
      component="span"
      className={
        styleChange === "removed"
          ? "font-semibold line-through"
          : "font-semibold"
      }
      style={{ color: styleChangeColor }}
    >
      {value}
    </Typography>
  );
};

const AdvancedValue: FC<{
  label: string;
  styleChange?: StyleChange;
}> = ({ label, styleChange }) => {
  const styleChangeColor = useStyleChangeColor(styleChange);

  return (
    <Typography
      component="span"
      variant="body2"
      className={styleChange === "removed" ? "line-through" : undefined}
      style={{ color: styleChangeColor }}
    >
      {label}
    </Typography>
  );
};

const AdvancedLine: FC<{ fields: AdvancedFieldHistory[] }> = ({ fields }) => {
  const { t } = useTranslation();

  return (
    <Typography
      variant="body2"
      component="div"
      className="flex flex-wrap items-center gap-x-1 gap-y-0.5"
    >
      <span className="uppercase">
        {t("components.categoryCollectionProfile.history.behavior.advanced")}
      </span>
      <span>
        <LocalizedList>
          {fields.map((field) => [
            field.key,
            <span
              key={field.key}
              className="inline-flex flex-wrap items-center gap-x-1"
            >
              <span>
                {t(
                  `views.categoryTemplates.shootTime.advancedFields.${field.key}.label`
                )}
              </span>
              <HistoryValue history={field}>
                {(value, { styleChange }) => (
                  <AdvancedValue
                    label={formatFormulaFieldValue(t, field.key, value)}
                    styleChange={styleChange}
                  />
                )}
              </HistoryValue>
            </span>,
          ])}
        </LocalizedList>
      </span>
    </Typography>
  );
};

const MostSimilarToHistoryRow: FC<{
  history: ValueHistory<ResolvedOriginCategory>;
}> = ({ history }) => {
  const { t } = useTranslation();

  if (history.change === "unchanged" && history.after === undefined) {
    return;
  }

  return (
    <div className="flex gap-3 items-start">
      <Typography className="font-medium min-w-[6rem] shrink-0">
        {t("views.categoryTemplates.shootTime.mostSimilarTo.label")}
      </Typography>
      <div className="min-w-0">
        <span className="inline-flex items-center border border-solid border-darken-200 dark:border-lighten-300 rounded-md px-1 py-0">
          <HistoryValue history={history}>
            {(value, { styleChange }) => (
              <AdvancedValue
                label={
                  value === "unknown"
                    ? t("utils.descriptors.unknown")
                    : translateCategoryName(t, value)
                }
                styleChange={styleChange}
              />
            )}
          </HistoryValue>
        </span>
      </div>
    </div>
  );
};

const formatBounds = (t: TFunction, bounds: BandBounds): string => {
  if (!Number.isFinite(bounds.upper)) {
    return t("utils.units.valuePlusWithUnit", {
      value: bounds.lower,
      unit: t("utils.units.mm"),
    });
  }
  return t("views.categoryTemplates.shootTime.bandRange", {
    lower: bounds.lower,
    upper: bounds.upper,
  });
};
